import { createIdempotencyKey } from "@lafayette/api-client";
import { STAFF_CATALOG_BOUNDS, type StaffCatalogNotes } from "@lafayette/schemas";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useRef, useState } from "react";
import { AsyncState } from "../../ui/async-state";
import { Button } from "../../ui/button";
import { cn } from "../../ui/cn";
import { SelectField, TextAreaField } from "../../ui/fields";
import { Notice } from "../../ui/notice";
import { SectionCard } from "../../ui/section-card";
import { StatusMessage } from "../../ui/status-message";
import type { CatalogCapabilities } from "../allowed-actions";
import type { SaveMessage } from "../commands";
import { actionRow, hintText, listRow, plainList } from "../components/styles";
import { describeCatalogError } from "../errors";
import { formatDateTime, noteKindLabels, noteKindOptions } from "../labels";
import { useCatalogRuntime } from "../runtime";

const notesKey = (itemId: string) => ["catalog", "notes", itemId] as const;

/**
 * Internal notes: staff only. The panel fetches its own list from the notes route (R3). The item detail
 * never carries notes, so this component takes no detail and renders nothing from one. The page does not
 * mount it for a role without the capability, so that role makes no notes request at all.
 */
export function InternalNotesPanel({
  itemId,
  capabilities,
}: Readonly<{ itemId: string; capabilities: CatalogCapabilities }>) {
  const { api } = useCatalogRuntime();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: notesKey(itemId),
    queryFn: ({ signal }) => api.listNotes(itemId, { signal }),
    retry: false,
    refetchOnWindowFocus: false,
  });
  const [kind, setKind] = useState("EDITORIAL");
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [message, setMessage] = useState<SaveMessage | null>(null);
  const [pending, setPending] = useState(false);
  const key = useRef<{ signature: string; value: string } | null>(null);

  async function add(event: FormEvent) {
    event.preventDefault();
    const text = body.trim();
    if (text === "") {
      setError("Enter the note.");
      return;
    }
    if (text.length > STAFF_CATALOG_BOUNDS.noteBody) {
      setError(`Use at most ${STAFF_CATALOG_BOUNDS.noteBody} characters.`);
      return;
    }
    setError(undefined);
    const signature = JSON.stringify([kind, text]);
    const idempotencyKey =
      key.current?.signature === signature ? key.current.value : createIdempotencyKey();
    key.current = { signature, value: idempotencyKey };
    setPending(true);
    setMessage(null);
    try {
      const notes = await api.addNote(
        itemId,
        { kind: kind as "EDITORIAL", body: text },
        { idempotencyKey },
      );
      key.current = null;
      queryClient.setQueryData<StaffCatalogNotes>(notesKey(itemId), notes);
      setBody("");
      setMessage({ tone: "success", text: "Note added." });
    } catch (cause) {
      const failure = describeCatalogError(cause);
      if (!failure.retryable) key.current = null;
      setMessage({ tone: "error", text: failure.message });
    } finally {
      setPending(false);
    }
  }

  return (
    <SectionCard
      id="internal-notes"
      title="Internal notes"
      description="Staff only. Customers never see these notes."
    >
      <Notice tone="info">Staff only. Do not copy a note into customer text.</Notice>
      {query.isPending ? (
        <AsyncState kind="loading" title="Loading notes" message="Reading the internal notes." />
      ) : query.isError ? (
        <AsyncState
          kind="error"
          title="Notes could not be loaded"
          message={describeCatalogError(query.error).message}
          onRetry={() => void query.refetch()}
        />
      ) : query.data.notes.length === 0 ? (
        <p className={hintText}>No internal notes yet.</p>
      ) : (
        <ol className={plainList}>
          {query.data.notes.map((note) => (
            <li key={note.id} className={cn(listRow, "grid gap-1")}>
              <p className={hintText}>
                <strong>{noteKindLabels[note.kind]}</strong> · staff{" "}
                {note.authorStaffUserId.slice(0, 8)} · {formatDateTime(note.createdAt)}
              </p>
              <p className="m-0 whitespace-pre-wrap">{note.body}</p>
            </li>
          ))}
        </ol>
      )}

      {capabilities.addNote ? (
        <form noValidate onSubmit={add} aria-label="Add an internal note" className="grid gap-4">
          <SelectField
            id="note-kind"
            label="Kind of note"
            value={kind}
            onChange={setKind}
            options={noteKindOptions}
          />
          <TextAreaField
            id="note-body"
            label="Note"
            value={body}
            onChange={setBody}
            error={error}
            rows={4}
            maxLength={STAFF_CATALOG_BOUNDS.noteBody}
            required
          />
          <div className={actionRow}>
            <Button type="submit" disabled={pending}>
              {pending ? "Adding…" : "Add note"}
            </Button>
          </div>
          <StatusMessage message={message} />
        </form>
      ) : null}
    </SectionCard>
  );
}
